import React, { useState, useEffect } from 'react'
import { 
  IonContent, 
  IonPage, 
  IonButton, 
  IonInput, 
  IonItem, 
  IonLabel,
  IonToast,
  IonText,
  IonIcon,
  IonSpinner
} from '@ionic/react'
import { useHistory, useLocation } from 'react-router-dom'
import { arrowBackOutline, eyeOutline, eyeOffOutline, checkmarkCircleOutline } from 'ionicons/icons'
import { supabase } from '../../services/supabase'

const ResetPasswordPage: React.FC = () => {
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastColor, setToastColor] = useState<'danger' | 'success'>('danger')
  const [isValidSession, setIsValidSession] = useState<boolean | null>(null)
  const [resetComplete, setResetComplete] = useState(false)
  
  const history = useHistory()
  const location = useLocation()

  useEffect(() => {
    // Check if we have a valid session from the email link
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession()
        
        if (error) {
          console.error('Session error:', error)
          setIsValidSession(false)
          return
        }

        if (session) {
          setIsValidSession(true)
        } else {
          // Try to get session from URL hash (for email links)
          const hashParams = new URLSearchParams(location.hash.substring(1))
          const accessToken = hashParams.get('access_token')
          const refreshToken = hashParams.get('refresh_token')
          
          if (accessToken && refreshToken) {
            const { error: sessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            })
            
            if (sessionError) {
              console.error('Set session error:', sessionError)
              setIsValidSession(false)
            } else {
              setIsValidSession(true)
            }
          } else {
            setIsValidSession(false)
          }
        }
      } catch (error) {
        console.error('Error checking session:', error)
        setIsValidSession(false)
      }
    }

    checkSession()
  }, [location])

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long'
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return 'Password must contain at least one lowercase letter'
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return 'Password must contain at least one uppercase letter'
    }
    if (!/(?=.*\d)/.test(password)) {
      return 'Password must contain at least one number'
    }
    return null
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!newPassword.trim() || !confirmPassword.trim()) {
      setToastMessage('Please fill in all fields')
      setToastColor('danger')
      setShowToast(true)
      return
    }

    if (newPassword !== confirmPassword) {
      setToastMessage('Passwords do not match')
      setToastColor('danger')
      setShowToast(true)
      return
    }

    const passwordError = validatePassword(newPassword)
    if (passwordError) {
      setToastMessage(passwordError)
      setToastColor('danger')
      setShowToast(true)
      return
    }

    setLoading(true)
    
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      })

      if (error) throw error

      setToastMessage('Password updated successfully!')
      setToastColor('success')
      setShowToast(true)
      setResetComplete(true)
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        history.replace('/login')
      }, 3000)
      
    } catch (error: any) {
      console.error('Password reset error:', error)
      setToastMessage(error.message || 'Failed to reset password')
      setToastColor('danger')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  if (isValidSession === null) {
    return (
      <IonPage>
        <IonContent className="ion-padding">
          <div className="min-h-screen flex items-center justify-center">
            <div className="text-center">
              <IonSpinner name="crescent" className="w-8 h-8 text-primary-600" />
              <p className="mt-4 text-gray-600">Verifying reset link...</p>
            </div>
          </div>
        </IonContent>
      </IonPage>
    )
  }

  if (isValidSession === false) {
    return (
      <IonPage>
        <IonContent className="ion-padding">
          <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-500 to-primary-700">
            <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
              <div className="mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <IonIcon icon={arrowBackOutline} className="text-2xl text-red-600" />
                </div>
                <h1 className="text-2xl font-heading font-bold text-gray-900 mb-2">
                  Invalid Reset Link
                </h1>
                <p className="text-gray-600 mb-6">
                  This password reset link is invalid or has expired. Please request a new one.
                </p>
              </div>
              
              <div className="space-y-4">
                <IonButton
                  expand="block"
                  onClick={() => history.push('/login')}
                >
                  Back to Login
                </IonButton>
                <IonButton
                  expand="block"
                  fill="outline"
                  onClick={() => history.push('/')}
                >
                  Go to Home
                </IonButton>
              </div>
            </div>
          </div>
        </IonContent>
      </IonPage>
    )
  }

  if (resetComplete) {
    return (
      <IonPage>
        <IonContent className="ion-padding">
          <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-500 to-primary-700">
            <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
              <div className="mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <IonIcon icon={checkmarkCircleOutline} className="text-2xl text-green-600" />
                </div>
                <h1 className="text-2xl font-heading font-bold text-gray-900 mb-2">
                  Password Reset Complete
                </h1>
                <p className="text-gray-600 mb-6">
                  Your password has been successfully updated. You will be redirected to the login page shortly.
                </p>
              </div>
              
              <IonButton
                expand="block"
                onClick={() => history.push('/login')}
              >
                Continue to Login
              </IonButton>
            </div>
          </div>
        </IonContent>
      </IonPage>
    )
  }

  return (
    <IonPage>
      <IonContent className="ion-padding">
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-500 to-primary-700">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-heading font-bold text-gray-900 mb-2">
                Reset Your Password
              </h1>
              <p className="text-gray-600">
                Enter your new password below
              </p>
            </div>

            <form onSubmit={handleResetPassword} className="space-y-6">
              <IonItem className="rounded-lg">
                <IonLabel position="stacked">New Password</IonLabel>
                <IonInput
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onIonInput={(e) => setNewPassword(e.detail.value!)}
                  required
                  placeholder="Enter new password"
                  autocomplete="new-password"
                />
                <IonButton
                  fill="clear"
                  slot="end"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <IonIcon icon={showPassword ? eyeOffOutline : eyeOutline} />
                </IonButton>
              </IonItem>

              <IonItem className="rounded-lg">
                <IonLabel position="stacked">Confirm New Password</IonLabel>
                <IonInput
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onIonInput={(e) => setConfirmPassword(e.detail.value!)}
                  required
                  placeholder="Confirm new password"
                  autocomplete="new-password"
                />
                <IonButton
                  fill="clear"
                  slot="end"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  <IonIcon icon={showConfirmPassword ? eyeOffOutline : eyeOutline} />
                </IonButton>
              </IonItem>

              <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                <p className="font-medium mb-2">Password Requirements:</p>
                <ul className="space-y-1 text-xs">
                  <li className={newPassword.length >= 8 ? 'text-green-600' : 'text-gray-500'}>
                    • At least 8 characters
                  </li>
                  <li className={/(?=.*[a-z])/.test(newPassword) ? 'text-green-600' : 'text-gray-500'}>
                    • One lowercase letter
                  </li>
                  <li className={/(?=.*[A-Z])/.test(newPassword) ? 'text-green-600' : 'text-gray-500'}>
                    • One uppercase letter
                  </li>
                  <li className={/(?=.*\d)/.test(newPassword) ? 'text-green-600' : 'text-gray-500'}>
                    • One number
                  </li>
                </ul>
              </div>

              <IonButton
                expand="block"
                type="submit"
                disabled={loading || !newPassword.trim() || !confirmPassword.trim()}
                className="mt-6"
              >
                {loading ? 'Updating Password...' : 'Update Password'}
              </IonButton>
            </form>

            <div className="mt-6 text-center">
              <IonText color="medium">
                Remember your password?{' '}
                <IonButton
                  fill="clear"
                  size="small"
                  onClick={() => history.push('/login')}
                  className="text-primary-600"
                >
                  Back to Login
                </IonButton>
              </IonText>
            </div>
          </div>
        </div>

        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={4000}
          color={toastColor}
        />
      </IonContent>
    </IonPage>
  )
}

export default ResetPasswordPage 